import { useInfiniteQuery } from '@tanstack/react-query';
import { getConversations, searchConversations } from '../services/conversation.service';
import { conversationKeys } from './conversation-keys';

/**
 * Hook for fetching paginated conversation list with optional client-side search
 *
 * @param search - Optional search term for client-side filtering
 * @returns Infinite query with conversation pages
 */
export const useConversations = () => {
  return useInfiniteQuery({
    queryKey: conversationKeys.list(),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      getConversations({ pageToken: pageParam, pageSize: 50 }),
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    staleTime: 6000,
    retry: 1,
    refetchOnWindowFocus: false,
    initialPageParam: undefined as string | undefined
  });
};

/**
 * Hook for remote full-text conversation search with infinite scroll
 *
 * **Usage:**
 * ```typescript
 * const isRemoteSearchActive = isSearchFocused && searchTerm.length >= 3 && activeTab === 0;
 * const { data, fetchNextPage, hasNextPage, isFetching, isLoading, error } =
 *   useConversationSearch(searchTerm, isRemoteSearchActive);
 * ```
 *
 * **Features:**
 * - Remote search via Search Service API (/v1/search/conversations)
 * - Infinite scroll pagination (20 items per page)
 * - Auto-cancellation on query changes (React Query)
 * - Minimum 3 characters required
 * - Authenticated with X-User-Id header
 *
 * @param query - Search term (min 3 characters)
 * @param enabled - Enable query (should check focus, length >= 3, and active tab)
 * @returns Infinite query with search result pages
 */
export const useConversationSearch = (query: string, enabled: boolean) => {
  return useInfiniteQuery({
    queryKey: conversationKeys.search(query),
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      searchConversations({ query, page: pageParam, perPage: 20 }),
    getNextPageParam: (lastPage) => {
      const hasMore = lastPage.page * lastPage.perPage < lastPage.totalCount;
      return hasMore ? lastPage.page + 1 : undefined;
    },
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
    enabled: enabled && query.length >= 3,
    initialPageParam: 1
  });
};
